import React from 'react';
import {
  Button, ChatBubble, ChatComposer, ChatDayDivider, ChatThread,
  EmptyState, PageHeader, SearchInput, useToast,
} from '@parsina/framework';
import { ConversationListSkeleton, ThreadSkeleton } from './MessagesSkeleton';
import { ArrowRight, FilterX, Inbox, MessageSquare, SearchX } from 'lucide-react';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import SegmentedControl from '../../components/common/SegmentedControl';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { MESSAGE_STATUS, messageStatusOf } from '../../constants/messageStatus';
import ConversationRow from './ConversationRow';
import { useMessages } from './hooks/useMessages';
import { groupTimelineByDay, readOnlyNoticeKey } from './Messages.shared';

// ─────────────────────────────────────────────────────────────────────────────
// Messages — the desktop conversation workspace (Archetype B, zone ⑤ as a P4
// two-pane).
//
// Two tiers, one rule: the URL decides what is on screen.
//   >1180px  — list pane (own scroll) + thread pane side by side. `?c=` only
//              moves the highlight.
//   769–1180 — ONE pane at a time, chosen by `?c=`. The other pane is not
//              rendered, never merely hidden with CSS: a display:none list is
//              still in the accessibility tree, still tabbable, and still
//              fetching — a second screen pretending not to exist.
//
// The list keeps working when the thread cannot: a dead homeserver session
// replaces the THREAD pane only (§2.4). The doctor can still see who wrote.
// ─────────────────────────────────────────────────────────────────────────────

const SEARCH_DEBOUNCE_MS = 300;

// ── Thread ───────────────────────────────────────────────────────────────────

const ThreadPane = ({ m, compact }) => {
  const { t, formatTime, formatDate } = useTranslation();
  const toast = useToast();
  const [draft, setDraft] = React.useState('');

  // A draft belongs to the conversation it was typed in.
  React.useEffect(() => { setDraft(''); }, [m.selectedRoomId]);

  const onSend = async () => {
    const body = draft;
    const ok = await m.send(body);
    // No success toast — the bubble appearing IS the confirmation. A failure
    // keeps the draft and says so, because the bubble alone is easy to miss.
    if (!ok) toast.error(t('messages.errors.send'));
    else setDraft('');
  };

  const groups = React.useMemo(() => groupTimelineByDay(m.timeline), [m.timeline]);

  const state = !m.selectedRoomId ? 'empty'
    : m.chatError ? 'error'
      : !m.chatReady ? 'loading'
        : m.timeline.length === 0 ? 'empty' : 'content';

  // On the compact tier this pane IS the screen, so the way back belongs to it,
  // as the first element of its own header strip (§A.4). A top-level
  // `PageHeader` never carries a back affordance — you cannot go "back" from a
  // root — so the control moved down here rather than into its actions slot.
  const backStrip = compact && m.selectedRoomId ? (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0,
      padding: '0.5rem 0.625rem',
      borderBottom: `1px solid ${C.border}`,
    }}>
      <Button
        variant="ghost"
        size="sm"
        className="min-h-11"
        onClick={m.clearSelection}
        data-testid="messages-header-back"
      >
        <ArrowRight size={16} aria-hidden />
        {t('common.actions.back')}
      </Button>
      <span
        dir="auto"
        data-testid="messages-header-peer"
        style={{
          flex: 1, minWidth: 0,
          fontSize: '0.9375rem', fontWeight: 700, color: C.textPrimary,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {m.selected?.peerDisplayName || t('messages.unknownContact')}
      </span>
    </div>
  ) : null;

  // ONE node owns the region's §7 state for all four states —
  // `messages-thread-region`. `messages-thread` stays on whatever renders the
  // messages, because the kit derives `messages-thread-load-older` from it and
  // the kit owns `ChatThread`'s own data-state (ready/loading-older).
  const region = (body) => (
    <div
      data-testid="messages-thread-region"
      data-state={state}
      aria-busy={state === 'loading' || undefined}
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {body}
    </div>
  );

  const shell = (children) => (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%',
      background: C.card, border: `1px solid ${C.border}`, borderRadius: '0.875rem',
      overflow: 'hidden',
    }}>
      {backStrip}
      {children}
    </div>
  );

  if (state !== 'content') {
    const body = state === 'error' ? (
      <ErrorState
        error={m.chatError}
        title={t('messages.thread.unavailable')}
        onRetry={m.reloadThread}
      />
    ) : state === 'loading' ? (
      <ThreadSkeleton />
    ) : (
      <EmptyState
        fill
        icon={<MessageSquare size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
        title={m.selectedRoomId ? t('messages.thread.empty.title') : t('messages.thread.select.title')}
        description={m.selectedRoomId ? t('messages.thread.empty.hint') : t('messages.thread.select.hint')}
      />
    );

    return shell(
      <>
        {region(
          <div
            data-testid="messages-thread"
            data-state={state}
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
          >
            {body}
          </div>,
        )}
        {m.selectedRoomId && state !== 'error' && (
          <Composer m={m} draft={draft} setDraft={setDraft} onSend={onSend} />
        )}
      </>,
    );
  }

  return shell(
    <>
      {region(
        <ChatThread
          // A thread is per-conversation state — sampled scroll gap, previous
          // child count, the node's own scrollTop. Switching rooms must build a
          // NEW one, or the second conversation opens wherever the first was
          // left.
          key={m.selectedRoomId}
          data-testid="messages-thread"
          fill
          hasOlder={m.hasOlder}
          loadingOlder={m.loadingOlder}
          onLoadOlder={m.loadOlder}
          loadOlderLabel={t('messages.thread.loadOlder')}
          padBlockRem={1}
        >
          {groups.flatMap((group) => [
            <ChatDayDivider key={`d-${group.dayTs}`} label={formatDate(group.dayTs, 'medium')} />,
            ...group.messages.map((msg) => {
              const statusKey = messageStatusOf(msg);
              return (
                <ChatBubble
                  key={msg.txnId || msg.eventId}
                  data-testid={`messages-bubble-${msg.txnId || msg.eventId}`}
                  body={msg.body}
                  mine={msg.mine}
                  pending={msg.pending}
                  failed={msg.failed}
                  timeLabel={formatTime(msg.ts)}
                  // The kit draws the glyph; the app supplies only the word for
                  // it. No app-side dot — a hue is not a delivery state.
                  status={msg.mine ? MESSAGE_STATUS[statusKey].bubble : undefined}
                  statusLabel={msg.mine ? t(MESSAGE_STATUS[statusKey].labelKey) : undefined}
                  onRetry={msg.failed ? () => m.retry(msg.txnId) : undefined}
                  retryLabel={t('messages.retry')}
                />
              );
            }),
          ])}
        </ChatThread>,
      )}
      <Composer m={m} draft={draft} setDraft={setDraft} onSend={onSend} />
    </>,
  );
};

const Composer = ({ m, draft, setDraft, onSend }) => {
  const { t } = useTranslation();
  return (
    <ChatComposer
      data-testid="messages-composer"
      inputProps={{ 'data-testid': 'messages-composer-input' }}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onSend={onSend}
      sending={m.sending}
      placeholder={t('messages.composer.placeholder')}
      sendLabel={t('messages.composer.send')}
      readOnlyNotice={m.canSend ? undefined : t(readOnlyNoticeKey(m.readOnlyReason))}
    />
  );
};

// ── List ─────────────────────────────────────────────────────────────────────

// Same window as the phone (Messages.mobile.jsx). A clinic with 400 linked
// patients would otherwise render 400 rows into the pane on first paint:
// "long same-page lists cap at a sane window («نمایش بیشتر» expander) rather
// than rendering unbounded DOM" (DESIGN-CONCEPT §6). Pagination, not infinite
// scroll — growth is an explicit tap.
const PAGE_SIZE = 20;

const ListPane = ({ m, onOpen }) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  // A new filter or search is a new list; showing page 4 of it would be lying
  // about where the user is.
  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [m.query, m.unreadOnly]);

  const isEmpty = !m.loading && !m.error && m.rows.length === 0;
  const state = m.loading ? 'loading'
    : m.error ? 'error'
      : isEmpty ? 'empty' : 'content';
  // Which EMPTY it is only changes the copy — `data-state` keeps the four
  // enumerated values (§15.1), so a [data-state="empty"] selector still
  // matches with a filter on. The app's own Encounters/Accounting screens do
  // exactly this.
  const filteredEmpty = isEmpty && m.hasActiveFilters;

  return (
    <div
      data-testid="messages-list"
      data-state={state}
      aria-label={t('messages.list.label')}
      aria-busy={m.loading || m.refreshing || undefined}
      data-refreshing={m.refreshing ? 'true' : undefined}
      style={{
        flex: 1, minHeight: 0, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: '0.5rem',
        paddingInlineEnd: '0.25rem',
        // A refetch keeps the rows and dims them; it never blanks them back to
        // skeletons. Only the first load has nothing to show.
        opacity: m.refreshing ? 0.45 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      {state === 'loading' && <ConversationListSkeleton count={5} avatar={38} />}

      {state === 'error' && (
        <ErrorState
          title={t('messages.list.unavailable')}
          error={m.error}
          onRetry={m.reload}
        />
      )}

      {state === 'empty' && filteredEmpty && (
        <EmptyState
          icon={<SearchX size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
          title={t('messages.list.filteredEmpty.title')}
          description={t('messages.list.filteredEmpty.hint')}
          action={
            <Button variant="outline" size="sm" onClick={m.clearFilters} data-testid="messages-list-empty-clear">
              <FilterX size={15} aria-hidden />
              {t('messages.clearFilters')}
            </Button>
          }
        />
      )}

      {state === 'empty' && !filteredEmpty && (
        <EmptyState
          icon={<Inbox size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
          title={t('messages.list.empty.title')}
          description={t('messages.list.empty.hint')}
        />
      )}

      {state === 'content' && m.rows.slice(0, visibleCount).map((row) => (
        <ConversationRow
          key={row.roomId || row.key}
          row={row}
          active={row.roomId === m.selectedRoomId}
          onOpen={onOpen}
        />
      ))}

      {state === 'content' && m.rows.length > visibleCount && (
        <Button
          variant="ghost"
          className="min-h-11 w-full"
          data-testid="messages-show-more"
          onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
        >
          {t('messages.list.showMore', { count: m.rows.length - visibleCount })}
        </Button>
      )}
    </div>
  );
};

// ── Toolbar (zone ④) ─────────────────────────────────────────────────────────
// One card row. It belongs to the LIST, so on the compact tier it appears and
// disappears with it — filtering a pane you cannot see is not a control.

const Toolbar = ({ m }) => {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState(m.query);
  const debounceRef = React.useRef(null);

  // Back/forward or a shared link changed `q` outside the box — resync it.
  React.useEffect(() => { setSearch(m.query); }, [m.query]);
  React.useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onSearch = (value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    if (value === '') { m.setSearch(''); return; }
    debounceRef.current = setTimeout(() => m.setSearch(value), SEARCH_DEBOUNCE_MS);
  };

  const clear = () => { setSearch(''); m.clearFilters(); };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap',
      background: C.card, border: `1px solid ${C.border}`, borderRadius: '0.875rem',
      padding: '0.625rem 0.75rem',
    }}>
      <SearchInput
        data-testid="messages-toolbar-search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={t('messages.search.placeholder')}
        clearLabel={t('common.actions.clearSearch')}
        style={{ flex: 1, minWidth: '10rem' }}
      />
      <SegmentedControl
        testId="messages-filter"
        ariaLabel={t('messages.filters.label')}
        items={[
          { id: 'all', label: t('messages.filters.all') },
          { id: 'unread', label: t('messages.filters.unread') },
        ]}
        value={m.unreadOnly ? 'unread' : 'all'}
        onChange={(id) => m.setUnreadOnly(id === 'unread')}
      />
      {m.hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={clear} data-testid="messages-toolbar-clear">
          <FilterX size={15} aria-hidden />
          {t('messages.clearFilters')}
        </Button>
      )}
    </div>
  );
};

// ── Screen ───────────────────────────────────────────────────────────────────

export default function MessagesWeb() {
  const { t } = useTranslation();
  const toast = useToast();
  const m = useMessages();

  // The compact tier collapses the two panes into one; the wide tier shows both.
  const [compact, setCompact] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 1180px)');
    const sync = () => setCompact(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const showThread = !compact || Boolean(m.selectedRoomId);
  const showList = !compact || !m.selectedRoomId;

  const onOpen = async (row) => {
    try {
      await m.openConversation(row);
    } catch (err) {
      console.error('openChatConversation failed:', err);
      toast.error(t('messages.errors.open'));
    }
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', background: C.bg,
    }}>
      {/* /messages is a top-level destination, so its header is the page's
          title + subtitle on EVERY tier and carries no back affordance — you
          cannot go "back" from a root (§2.2/§137). Naming the counterpart and
          returning to the list are the thread pane's own business; on the
          compact tier it renders both in its leading strip. */}
      <PageHeader title={t('messages.title')} sub={t('messages.subtitle')} />

      <div style={{
        flex: 1, minHeight: 0, display: 'grid', gap: '1rem', padding: '1rem',
        gridTemplateColumns: compact ? 'minmax(0, 1fr)' : '22rem minmax(0, 1fr)',
      }}>
        {showList && (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, gap: '0.75rem' }}>
            <Toolbar m={m} />
            <ListPane m={m} onOpen={onOpen} />
          </div>
        )}
        {/* Keyed on the room for the same reason `ChatThread` is: everything the
            pane holds — the draft, the thread's scroll position — belongs to
            one conversation. */}
        {showThread && <ThreadPane key={m.selectedRoomId || 'none'} m={m} compact={compact} />}
      </div>
    </div>
  );
}
